import { useCallback, useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import Divisions from '../../shared/divisions';
import CustomFormDrawer from '../../shared/customFormDrawer';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import DotsMenu from '../../shared/dotsMenu';
import AlertDialog from '../../shared/alertDialog';
import UserCount from '../../shared/userCount';
import RichTextPreview from '../../shared/richTextPreview';
import {
  API_USERS_MULTIPLE,
  API_DIVISIONS,
  api_division,
  api_department,
  API_DEPARTMENTS,
  API_DIVISION_ITEMS_REORDER,
  api_departments_users,
  api_departments_reorder,
  api_department_duplicate,
  api_division_duplicate,
} from '../../../constants/apiRoutes';
import { USER_EMPLOYMENT_INFORMATION } from '../../../constants/people';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { getAllUsers, clearAllUsers } from '../../../store/modules/people';
import {
  clearDivisionsAndDepartments,
  getDivisionsAndDepartments,
  setDepartmentsReordering,
  setDivisionsReordering,
} from '../../../store/modules/divisionsAndDepartments';
import http from '../../../utility/http';
import {
  prepareUsersForUpdate,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { useTranslations } from '../../../utility/useTranslations';
import { getObjectToNumberArray } from '../../../utility/helpers';
import {
  hasAuthPermissionsCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  DIVISION_FIELDS,
  prepareAssignedUsersToDepartmentFields,
  prepareDepartmentFields,
  prepareMenuItems,
  prepareFieldsToDuplicate,
  prepareInitialUsersData,
  prepareDivisionsTableData,
  prepareInitialData,
  prepareInitialDataToDuplicate,
} from '../../../utility/divisions';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  subHeader: {
    padding: spacing(8, 0, 4),
    ...sticky(primary.white, 105),
  },
  divisionPageContainer: {
    width: 'inherit',
  },
  actionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 32,
  },
  descriptionText: {
    color: primary.black,
    '& a': {
      color: primary.black,
    },
  },
  descriptionTitle: {
    color: primary.black,
    fontSize: 16,
    height: 22,
    lineHeight: '18px',
    marginBottom: 12,
  },
  drawerContent: {
    height: '100%',
  },
}));

const DELETE_TYPE = {
  DIVISION: 'DIVISION',
  DEPARTMENT: 'DEPARTMENT',
};

const DivisionsPage = ({
  navigate,
  auth,
  accessControl,
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.DIVISIONS);

  const { divisions } = useSelector(divisionsAndDepartmentsSelector);
  const { allUsers } = useSelector(peopleSelector);

  const [deleteType, setDeleteType] = useState('');
  const [isDivisionDrawerOpened, setIsDivisionDrawerOpened] = useState(false);
  const [isDepartmentDrawerOpened, setIsDepartmentDrawerOpened] =
    useState(false);
  const [isDuplicateDivisionDrawerOpened, setIsDuplicateDivisionDrawerOpened] =
    useState(false);
  const [
    isDuplicateDepartmentDrawerOpened,
    setIsDuplicateDepartmentDrawerOpened,
  ] = useState(false);
  const [isDeleteDialogOpened, setIsDeleteDialogOpened] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState({});
  const [alertMessage, setAlertMessage] = useState({});
  const [isAssignUsersToDepartmentOpened, setIsAssignUsersToDepartmentOpened] =
    useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const [canEdit, canDelete, canManageUsers] = hasAuthPermissionsCheck(
    [
      PERMISSION_CODES.DEPARTMENTS_EDIT,
      PERMISSION_CODES.DEPARTMENTS_DELETE,
      PERMISSION_CODES.DEPARTMENTS_USERS_MANAGE,
    ],
    accessControl
  );

  const goToUserProfilePage = userId => navigate(`/people/${userId}`);

  const initialData = useCallback(() => {
    getDivisionsAndDepartments(dispatch);
    if (canManageUsers) {
      getAllUsers(dispatch, {
        [PARAMS.RBAC_SCOPE_PERMISSION]:
          PERMISSION_CODES.DEPARTMENTS_USERS_MANAGE,
      });
    }
  }, [dispatch, canManageUsers]);

  useEffect(() => {
    initialData();
  }, [initialData]);

  const cleanup = useCallback(() => {
    dispatch(clearAllUsers());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleSaveDivision = division => {
    if (isEdit) {
      return http
        .patch(api_division(division.id), {
          name: division.name,
        })
        .then(() => {
          showSuccessMessage(translations.successMessages.divisionUpdated);
          getDivisionsAndDepartments(dispatch);
        });
    }
    return http
      .post(API_DIVISIONS, {
        name: division.name,
      })
      .then(() => {
        showSuccessMessage(translations.successMessages.divisionSaved);
        getDivisionsAndDepartments(dispatch);
      });
  };

  const handleSaveDepartment = department => {
    if (isEdit) {
      return http
        .patch(api_department(department.id), {
          name: department.name,
          description: department.description,
        })
        .then(() => {
          showSuccessMessage(translations.successMessages.departmentUpdated);
          getDivisionsAndDepartments(dispatch);
        });
    }
    return http
      .post(API_DEPARTMENTS, {
        division: selectedDivision?.id,
        name: department.name,
        description: department.description,
      })
      .then(() => {
        showSuccessMessage(translations.successMessages.departmentSaved);
        getDivisionsAndDepartments(dispatch);
      });
  };

  const toggleAssignUsersToDepartmentDrawer = () =>
    setIsAssignUsersToDepartmentOpened(prevIsOpened => !prevIsOpened);

  const handleToggleDivisionDrawer = () => {
    setIsDivisionDrawerOpened(prevState => !prevState);
  };

  const handleCloseDivisionDrawer = () => {
    handleToggleDivisionDrawer();
    setIsEdit(false);
    setSelectedDivision({});
  };

  const handleEditDivisionTitle = division => {
    setSelectedDivision(division);
    setIsEdit(true);
    handleToggleDivisionDrawer();
  };

  const handleDepartmentDrawer = division => {
    setSelectedDivision(division);
    setIsDepartmentDrawerOpened(prevState => !prevState);
  };

  const handleEditDepartment = (department, division) => {
    setSelectedDepartment(department);
    setSelectedDivision(division);
    setIsEdit(true);
    setIsDepartmentDrawerOpened(prevState => !prevState);
  };

  const handleCloseDepartmentDrawer = () => {
    setIsDepartmentDrawerOpened(prevState => !prevState);
    setIsEdit(false);
    setSelectedDepartment({});
    setSelectedDivision({});
  };

  const handleDeleteDialog = type => {
    switch (type) {
      case DELETE_TYPE.DIVISION: {
        setAlertMessage(translations.deleteDivisionDialog);
        break;
      }
      case DELETE_TYPE.DEPARTMENT: {
        setAlertMessage(translations.deleteDepartmentDialog);
        break;
      }
      default:
        break;
    }
    setDeleteType(type);
    setIsDeleteDialogOpened(prevState => !prevState);
  };

  const handleConfirmDelete = () => {
    switch (deleteType) {
      case DELETE_TYPE.DIVISION:
        return http
          .delete(`${API_DIVISIONS}${selectedDivision?.id}/`)
          .then(() => {
            showSuccessMessage(translations.successMessages.divisionDeleted);
            setIsDivisionDrawerOpened(false);
            setIsDeleteDialogOpened(prevState => !prevState);
            getDivisionsAndDepartments(dispatch);
          });
      case DELETE_TYPE.DEPARTMENT:
        return http
          .delete(`${API_DEPARTMENTS}${selectedDepartment?.id}/`)
          .then(() => {
            showSuccessMessage(translations.successMessages.departmentDeleted);
            setIsDepartmentDrawerOpened(false);
            setIsDeleteDialogOpened(prevState => !prevState);
            getDivisionsAndDepartments(dispatch);
          });
      default:
        break;
    }
  };

  const handleReorderDivisions = reorderedDivisions => {
    const divisionIds = getObjectToNumberArray(reorderedDivisions);
    dispatch(setDivisionsReordering({ divisionIds }));
    http.patch(API_DIVISION_ITEMS_REORDER, {
      order: divisionIds,
    });
  };

  const handleReorderDepartments = (divisionId, reorderedDepartments) => {
    const departmentIds = getObjectToNumberArray(reorderedDepartments);
    const divisionIndex = divisions.findIndex(
      division => division?.id === divisionId
    );
    dispatch(setDepartmentsReordering({ divisionIndex, departmentIds }));
    return http.patch(api_departments_reorder(divisionId), {
      order: departmentIds,
    });
  };

  const renderDescription = department => {
    return (
      <>
        <TextBoxWithTooltip
          className={classes.descriptionTitle}
          variant="h5"
          text={department.name}
        />
        <RichTextPreview
          className={classes.descriptionText}
          variant="body1"
          text={department.description}
        />
      </>
    );
  };

  const handleAssignUsersToDepartmentDialog = async (department, division) => {
    const { data } = await http.get(api_departments_users(department?.id));
    setSelectedDepartment(department);
    setSelectedDivision(division);
    setAssignableUsers(data);
    toggleAssignUsersToDepartmentDrawer();
  };

  const handleCloseAssignUsersDrawer = () => {
    toggleAssignUsersToDepartmentDrawer();
    setSelectedDepartment({});
    setSelectedDivision({});
  };

  const handleAssignUsersToDepartment = async ({ assignUsers }) => {
    await http.patch(
      API_USERS_MULTIPLE,
      prepareUsersForUpdate(
        allUsers,
        assignUsers,
        assignableUsers,
        selectedDepartment.id,
        USER_EMPLOYMENT_INFORMATION.DEPARTMENT
      )
    );
    getDivisionsAndDepartments(dispatch);
    showSuccessMessage(translations.successMessage.assignedUsersToDepartment);
  };

  const handleUsers = departmentId =>
    http.get(api_departments_users(departmentId));

  const canAccessUserProfileCheck = user =>
    isUserProfileAccessibleCheck(accessControl, auth.id, user.id);

  const renderPeople = (department, division) => {
    const departmentUsers = department.user_count;
    return (
      <UserCount
        actionButtonLabel={
          translations.divisionGroupView.assignUsersToDepartment.assignLabel
        }
        goToUserProfile={goToUserProfilePage}
        canAccessUserProfileCheck={canAccessUserProfileCheck}
        userCount={departmentUsers}
        getUsers={() => handleUsers(department.id)}
        onTooltipAction={() =>
          handleAssignUsersToDepartmentDialog(department, division)
        }
        hasActionButton={canManageUsers}
        isTooltipDisabled={!departmentUsers && !canManageUsers}
      />
    );
  };

  const handleDuplicateDepartmentDrawer = (department, division) => {
    setSelectedDivision(division);
    setSelectedDepartment(department);
    setIsDuplicateDepartmentDrawerOpened(prevState => !prevState);
  };

  const handleDuplicateDepartment = ({ name, division }) => {
    return http
      .post(api_department_duplicate(selectedDepartment?.id), {
        division_id: division,
        name,
      })
      .then(() => {
        getDivisionsAndDepartments(dispatch);
        showSuccessMessage(translations.successMessages.duplicateDepartment);
      });
  };

  const handleDeleteDivisionDialog = division => {
    setSelectedDivision(division);
    handleDeleteDialog(DELETE_TYPE.DIVISION);
  };

  const handleDeleteDepartmentDialog = department => {
    setSelectedDepartment(department);
    handleDeleteDialog(DELETE_TYPE.DEPARTMENT);
  };

  const renderActions = (department, division) => (
    <DotsMenu
      menuItems={prepareMenuItems(
        translations.divisionGroupView,
        department,
        division,
        canEdit,
        canDelete,
        handleDeleteDepartmentDialog,
        handleEditDepartment,
        handleDuplicateDepartmentDrawer
      )}
    />
  );

  const handleDuplicateDivisionDrawer = (division = {}, isOpened = false) => {
    setSelectedDivision(division);
    setIsDuplicateDivisionDrawerOpened(isOpened);
  };

  const handleDuplicateDivision = ({ name }) => {
    return http
      .post(api_division_duplicate(selectedDivision?.id), { name })
      .then(() => {
        getDivisionsAndDepartments(dispatch);
        setSelectedDivision({});
        setIsDuplicateDivisionDrawerOpened(false);
        showSuccessMessage(translations.successMessages.duplicateDivision);
      });
  };

  const departmentSubtitleText = `${selectedDivision?.name} >`;

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      shouldPassProps={false}
      navigate={navigate}
      dispatch={dispatch}
      isFullWidthContent
    >
      <div>
        <div className={classes.subHeader}>
          <div className={classes.actionWrapper}>
            <Typography variant="body2">
              {canEdit || canDelete
                ? translations.description.admin
                : translations.description.read}
            </Typography>
          </div>
        </div>
        <div className={classes.divisionPageContainer}>
          <Divisions
            translations={translations.divisionGroupView}
            divisions={prepareDivisionsTableData(
              divisions,
              renderDescription,
              renderPeople,
              renderActions
            )}
            canEdit={canEdit}
            canDelete={canDelete}
            onEditDivisionTitle={handleEditDivisionTitle}
            onDuplicateDivision={division =>
              handleDuplicateDivisionDrawer(division, true)
            }
            handleDeleteDivisionDialog={handleDeleteDivisionDialog}
            handleCreateDivision={handleToggleDivisionDrawer}
            handleCreateDepartment={handleDepartmentDrawer}
            onReorderDivision={handleReorderDivisions}
            onReorderDepartment={handleReorderDepartments}
          />
        </div>
        <CustomFormDrawer
          translations={translations.divisionGroupView.addDivision}
          isOpened={isDivisionDrawerOpened}
          isInitialValid={isEdit}
          initialData={prepareInitialData(selectedDivision, isEdit)}
          fields={DIVISION_FIELDS}
          hideDelete={!canDelete}
          onClose={handleCloseDivisionDrawer}
          onSave={handleSaveDivision}
          onDelete={() => handleDeleteDialog(DELETE_TYPE.DIVISION)}
        />
        <CustomFormDrawer
          translations={translations.divisionGroupView.addDepartment}
          isOpened={isDepartmentDrawerOpened}
          isInitialValid={isEdit}
          initialData={prepareInitialData(selectedDepartment, isEdit, true)}
          hideDelete={!canDelete}
          fields={prepareDepartmentFields(departmentSubtitleText)}
          onClose={handleCloseDepartmentDrawer}
          onSave={handleSaveDepartment}
          onDelete={() => handleDeleteDialog(DELETE_TYPE.DEPARTMENT)}
        />
        <CustomFormDrawer
          translations={translations.divisionGroupView.duplicateDivision}
          isOpened={isDuplicateDivisionDrawerOpened}
          initialData={prepareInitialData(selectedDivision, true)}
          fields={DIVISION_FIELDS}
          onClose={handleDuplicateDivisionDrawer}
          onSave={handleDuplicateDivision}
          hideDelete
        />
        <CustomFormDrawer
          translations={translations.divisionGroupView.duplicateDepartment}
          isOpened={isDuplicateDepartmentDrawerOpened}
          initialData={prepareInitialDataToDuplicate(
            selectedDivision,
            selectedDepartment
          )}
          fields={prepareFieldsToDuplicate(departmentSubtitleText)}
          onClose={() =>
            setIsDuplicateDepartmentDrawerOpened(prevState => !prevState)
          }
          divisions={divisions}
          onSave={handleDuplicateDepartment}
        />
        <CustomFormDrawer
          customContentClass={classes.drawerContent}
          translations={translations.divisionGroupView.assignUsersToDepartment}
          isOpened={isAssignUsersToDepartmentOpened}
          initialData={prepareInitialUsersData(assignableUsers)}
          fields={prepareAssignedUsersToDepartmentFields(
            departmentSubtitleText
          )}
          allUsers={allUsers}
          onClose={handleCloseAssignUsersDrawer}
          onSave={handleAssignUsersToDepartment}
          hasCancelButton
          hideDelete
          isInitialValid
        />
        <AlertDialog
          translations={alertMessage}
          isOpened={isDeleteDialogOpened}
          onClose={handleDeleteDialog}
          onConfirm={handleConfirmDelete}
          isWarning
        />
      </div>
    </PageContainer>
  );
};

DivisionsPage.propTypes = {
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(DivisionsPage);
